import { trackEventGA } from "../../libs/analyticsManager";

/**
 * Onboarding (Oct 2026): every eligible new desktop signup lands on
 * /starting-options for their FIRST post-signup redirect. Signup completes
 * once, so "first login only" needs no seen-stamp — every later login takes
 * the normal redirect.
 */
export const START_OPTIONS_EXPERIMENT = "onboarding-start-v1";
export const START_OPTIONS_PATH = "/starting-options";
export const DEFAULT_SIGNUP_LANDING = "/home/library";
/** Same breakpoint as HomeLayout / StartingOptions / the welcome-video gate. */
export const MOBILE_QUERY = "(max-width:900px)";

export type OnboardingArm = "start_options" | "library";

/**
 * Who lands on /starting-options: desktop, and headed for the default landing
 * (no real redirectTo — those users already came with an intent). Pure so the
 * rule is unit-tested; the caller reads the browser state.
 */
export function isStartOptionsEligible({
  uid,
  isMobile,
  redirectTo,
}: {
  uid: string | null | undefined;
  isMobile: boolean;
  redirectTo: string | null;
}): boolean {
  if (!uid || isMobile) return false;
  return !redirectTo || redirectTo === DEFAULT_SIGNUP_LANDING;
}

const readIsMobile = (): boolean => {
  try {
    return window.matchMedia(MOBILE_QUERY).matches;
  } catch {
    return true; // can't tell → treat as mobile, i.e. not enrolled
  }
};

/** sessionStorage marker: set as SignUp hands off to /starting-options, cleared
 *  once the arm is stamped there. Session-only, so typing the URL never stamps. */
export const PENDING_ARM_KEY = "onboardingArmPending";

/**
 * Where signup should land this user, or null for the normal redirect. No side
 * effects: SignUp calls it at redirect time, so a user who closes the Welcome
 * step is never counted or stamped.
 */
export function startOptionsLandingFor(
  uid: string | null | undefined,
): string | null {
  try {
    return isStartOptionsEligible({
      uid,
      isMobile: readIsMobile(),
      redirectTo: sessionStorage?.getItem("redirectTo") ?? null,
    })
      ? START_OPTIONS_PATH
      : null;
  } catch (error) {
    console.warn("[onboarding] landing check skipped:", error);
    return null;
  }
}

/** Called right before SignUp's hard navigation to /starting-options. */
export function beginStartOptionsLanding(): void {
  trackEventGA("onboarding_arm_assigned", {
    experiment: START_OPTIONS_EXPERIMENT,
    arm: "start_options",
  });
  try {
    sessionStorage.setItem(PENDING_ARM_KEY, START_OPTIONS_EXPERIMENT);
  } catch {
    // Blocked storage: no stamp, so the user just gets the welcome video later.
  }
}

const STAMP_ATTEMPTS = 6;

/**
 * Run by /starting-options once the profile has loaded: stamps the arm on the
 * profile (the source of truth — EU users without consent send no GA4), which
 * also skips the library welcome video. Stamping here rather than during signup
 * means the write never races the page unload, and only users who actually
 * landed are stamped. Never rejects.
 */
export async function stampPendingStartOptionsArm({
  uid,
  getUid,
  stampArm,
}: {
  uid: string;
  /** The signed-in uid now — never stamp an account that signed in mid-retry. */
  getUid: () => string | null | undefined;
  /** LoginState.markOnboardingArm — false means another write held the lock. */
  stampArm: (arm: OnboardingArm, experiment: string) => Promise<boolean>;
}): Promise<void> {
  let experiment: string | null = null;
  try {
    experiment = sessionStorage.getItem(PENDING_ARM_KEY);
  } catch {
    return;
  }
  if (!experiment) return;
  for (let attempt = 1; attempt <= STAMP_ATTEMPTS; attempt++) {
    if (getUid() !== uid) return;
    try {
      if (await stampArm("start_options", experiment)) {
        try {
          sessionStorage.removeItem(PENDING_ARM_KEY);
        } catch {
          // ignore
        }
        return;
      }
    } catch (error) {
      console.warn("[onboarding] arm stamp failed:", error);
    }
    if (attempt < STAMP_ATTEMPTS)
      await new Promise((resolve) => setTimeout(resolve, 500 * attempt));
  }
}
